(() => {
  'use strict';
  if (customElements.get('pg-deferred-reviews')) return;

  class DeferredReviews extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.lifecycle = new AbortController();
      const signal = this.lifecycle.signal;
      this.addEventListener('pointerdown', () => this.mount(), { once: true, signal });
      this.addEventListener('focusin', () => this.mount(), { once: true, signal });
      const observe = () => {
        if (!this.isConnected || this.mounted) return;
        if (!('IntersectionObserver' in window)) return this.schedule();
        this.visibility = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.schedule();
        }, { rootMargin: '100px' });
        this.visibility.observe(this);
      };
      if (document.readyState === 'complete') observe();
      else window.addEventListener('load', observe, { once: true, signal });
    }

    schedule() {
      if (this.scheduled || this.mounted) return;
      this.scheduled = true;
      this.visibility?.disconnect();
      // Give the painted hero a frame before the provider builds the carousel.
      this.frame = requestAnimationFrame(() => {
        if ('requestIdleCallback' in window) {
          this.idle = requestIdleCallback(() => this.mount(), { timeout: 1500 });
        } else this.timer = setTimeout(() => this.mount(), 0);
      });
    }

    mount() {
      if (this.mounted || !this.isConnected) return;
      const template = this.querySelector('template');
      if (!template) return;
      this.mounted = true;
      this.visibility?.disconnect();
      const fragment = document.importNode(template.content, true);
      const scripts = [...fragment.querySelectorAll('script')].map(original => {
        const marker = document.createComment('Judge.me initializer');
        original.replaceWith(marker);
        return { original, marker };
      });
      template.replaceWith(fragment);
      const summary = this.querySelector('.pg-deferred-reviews__summary');
      const widget = this.querySelector('.jdgm-testimonials-carousel');
      const update = () => {
        const ready = widget && !widget.classList.contains('jdgm-hidden') && widget.querySelector('.jdgm-testimonial');
        this.dataset.state = ready ? 'ready' : 'loading';
        if (summary) summary.hidden = !!ready || !!this.querySelector('.pg-home-reviews-summary');
        if (ready) this.readiness?.disconnect();
      };
      if (widget) {
        this.readiness = new MutationObserver(update);
        this.readiness.observe(widget, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
      }
      // Execute the app's original initializer once, without copying its logic.
      scripts.forEach(({ original, marker }) => {
        const script = document.createElement('script');
        [...original.attributes].forEach(attribute => script.setAttribute(attribute.name, attribute.value));
        script.textContent = original.textContent;
        marker.replaceWith(script);
      });
      update();
      this.dispatchEvent(new CustomEvent('pg:reviews:mounted', { bubbles: true }));
      // The existing provider guard supplies its own identical loading summary.
      if (summary && this.querySelector('.pg-home-reviews-summary')) summary.hidden = true;
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.visibility?.disconnect();
      this.readiness?.disconnect();
      cancelAnimationFrame(this.frame);
      if (this.idle !== undefined) cancelIdleCallback(this.idle);
      clearTimeout(this.timer);
      this.started = false;
      this.scheduled = false;
    }
  }
  customElements.define('pg-deferred-reviews', DeferredReviews);
})();
